import { useState, useEffect } from 'react';
import request from '../../../utils/request';
import { processAnswersForPayload, processAnswersFromAPI } from './utils';

export const useDevices = (devicesPerPage) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);

  const fetchDevices = async (page = currentPage) => {
    setLoading(true);
    try {
      const response = await request.get(`/orderDevices?page=${page}&limit=${devicesPerPage}`);
      setDevices(response.data.data || []);
      setTotalDevices(response.data.totalCount || 0);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const addDevice = async (deviceData) => {
    const payload = {
      device: {
        name: deviceData.name,
        deviceType: deviceData.deviceType,
        familyType: deviceData.familyType,
        deviceSku: deviceData.deviceSku,
        oneTimePrice: deviceData.oneTimePrice,
        easyPaymentPrice: deviceData.easyPaymentPrice
      },
      answers: processAnswersForPayload(deviceData.answers),
      createMissingQuestions: true
    };

    await request.post('/orderDevices', payload);
    await fetchDevices();
  };

  const updateDevice = async (deviceId, deviceData) => {
    const payload = {
      device: {
        name: deviceData.name,
        deviceType: deviceData.deviceType,
        familyType: deviceData.familyType,
        deviceSku: deviceData.deviceSku,
        oneTimePrice: deviceData.oneTimePrice,
        easyPaymentPrice: deviceData.easyPaymentPrice
      },
      answers: processAnswersForPayload(deviceData.answers),
      createMissingQuestions: true,
      replaceAll: false
    };

    await request.put(`/orderDevices/${deviceId}`, payload);
    await fetchDevices();
  };

  const deleteDevice = async (deviceId) => {
    await request.delete(`/orderDevices/${deviceId}`);
    await fetchDevices();
  };

  const handlePageChange = (page) => {
    fetchDevices(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  return {
    devices,
    loading,
    currentPage,
    totalPages,
    totalDevices,
    fetchDevices,
    addDevice,
    updateDevice,
    deleteDevice,
    handlePageChange,
    handlePreviousPage,
    handleNextPage
  };
};

export const useNotifications = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notification]);

  return { notification, showNotification };
};

export const useForm = () => {
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    deviceType: '',
    familyType: '',
    deviceSku: '',
    oneTimePrice: '',
    easyPaymentPrice: '',
    answers: {
      coperToFibreMigration: [],
      customerSegment: [],
      contractTerm: [],
      customerRequireAIFeatures: [],
      typeOfInstallation: [],
      businessConnectEdition: [],
      otherTelusServices: [],
      doesCustomerRequireSpareDevices: [],
      hardwareCredir: []
    }
  });

  const handleEdit = (device) => {
    setEditingId(device.id);
    
    // Process answers from API format to form format
    const processedAnswers = processAnswersFromAPI(device.answers);
    
    setFormValues({
      name: device.name || '',
      deviceType: device.deviceType || '',
      familyType: device.familyType || '',
      deviceSku: device.deviceSku || '',
      oneTimePrice: device.oneTimePrice || '',
      easyPaymentPrice: device.easyPaymentPrice || '',
      answers: processedAnswers
    });

    // Auto-scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('[data-form-section="device-form"]');
      if (formElement) {
        formElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormValues({
      name: '',
      deviceType: '',
      familyType: '',
      deviceSku: '',
      oneTimePrice: '',
      easyPaymentPrice: '',
      answers: {}
    });
  };

  return {
    editingId,
    formValues,
    handleEdit,
    resetForm,
    setFormValues
  };
};
